import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import isFunction from 'lodash/isFunction';
import { TweenMax } from 'gsap';
import classNames from 'classnames';

import createStatsFadeInAnimation from '../../animations/statsFadeIn';
import createStatsFadeOutAnimation from '../../animations/statsFadeOut';

const propTypes = {
    nowListening: PropTypes.string.isRequired,
    connecting: PropTypes.string.isRequired,
    aroundTheWorld: PropTypes.string.isRequired,
    count: PropTypes.number,
    realCount: PropTypes.number,
    // incrementSpeed: PropTypes.number,
    firstCountSpeed: PropTypes.number,
    countSpeed: PropTypes.number,
    onConnected: PropTypes.func,
    onCountChange: PropTypes.func,
    onCountStart: PropTypes.func,
    onClickCount: PropTypes.func,
};

const defaultProps = {
    count: -1,
    realCount: 0,
    incrementSpeed: 0.2,
    firstCountSpeed: 10,
    countSpeed: 10,
    onConnected: null,
    onCountChange: null,
    onCountStart: null,
    onClickCount: null,
};

class Stats extends Component {
    constructor(props) {
        super(props);

        this.onTextStart = this.onTextStart.bind(this);
        this.onTextComplete = this.onTextComplete.bind(this);
        this.onClickCount = this.onClickCount.bind(this);
        this.onFadeInComplete = this.onFadeInComplete.bind(this);

        this.timelineFadeIn = null;
        this.timelineFadeOut = null;
        this.updateCountTween = null;

        this.refContainer = null;
        this.refNowListening = null;
        this.refConnecting = null;
        this.refAroundTheWorld = null;
        this.refCount = null;
        this.refLabel = null;

        this.state = {
            count: props.count === -1 ? 1 : props.count,
            canClickCount: false,
        };
    }

    componentDidMount() {
        this.timelineFadeIn = createStatsFadeInAnimation({
            container: this.refContainer,
            texts: {
                nowListening: this.refNowListening,
                connecting: this.refConnecting,
                aroundTheWorld: this.refAroundTheWorld,
            },
            count: this.refCount,
            onTextStart: this.onTextStart,
            onTextComplete: this.onTextComplete,
            countTween: this.createCountTween(() => this.props.realCount),
        }).paused(true);

        this.timelineFadeIn.eventCallback('onComplete', this.onFadeInComplete);

        this.timelineFadeOut = createStatsFadeOutAnimation({
            label: this.refLabel,
            count: this.refCount,
        }).paused(true);
    }

    componentDidUpdate(prevProps) {
        const countChanged = prevProps.count !== this.props.count;
        if (countChanged) {
            this.updateCount(this.props.count);
        }
    }

    // eslint-disable-next-line
    onTextStart() {

    }

    onTextComplete(key) {
        const { onConnected } = this.props;
        if (key === 'connecting' && onConnected) {
            onConnected();
        }
    }

    onFadeInComplete() {
        this.setState({
            canClickCount: true,
        });
    }

    onCountStart(count) {
        const { onCountStart } = this.props;
        if (onCountStart) {
            onCountStart(count);
        }
    }

    onCountChange(count, delta) {
        const { onCountChange } = this.props;
        if (onCountChange) {
            onCountChange(count, delta);
        }
    }

    onClickCount() {
        const { onClickCount } = this.props;
        if (onClickCount !== null) {
            onClickCount();
        }
    }

    getTimelineFadeIn() {
        return this.timelineFadeIn;
    }

    getTimelineFadeOut() {
        return this.timelineFadeOut;
    }

    getContainerRef() {
        return this.refContainer;
    }

    updateCount(newCount) {
        if (this.updateCountTween !== null) {
            this.updateCountTween.kill();
            this.updateCountTween = null;
        }
        this.updateCountTween = this.createCountTween(newCount);
    }

    createCountTween(newCount) {
        const { /* incrementSpeed, */ firstCountSpeed, countSpeed } = this.props;
        const isDynamic = isFunction(newCount);
        let dynamicNewCount;
        const startCount = this.state.count === -1 ? 1 : this.state.count;
        const from = isDynamic ? {
            progress: 0,
        } : {
            count: startCount,
        };
        const to = isDynamic ? {
            progress: 1,
        } : {
            count: newCount,
        };
        let lastCountChangeCount = startCount;
        // const duration = isDynamic ?
        //         firstCountSpeed : (Math.abs(newCount - startCount) * incrementSpeed);
        const duration = isDynamic ? firstCountSpeed : countSpeed;
        return TweenMax.to(from, duration, {
            ...to,
            onStart: () => {
                if (isDynamic) {
                    dynamicNewCount = newCount();
                    this.onCountStart(dynamicNewCount);
                }
            },
            onUpdate: () => {
                let count;
                if (isDynamic) {
                    const delta = from.progress * (dynamicNewCount - startCount);
                    count = Math.round(startCount + delta);
                } else {
                    count = Math.round(from.count);
                }
                if (count !== this.state.count) {
                    this.setState(() => ({
                        count,
                    }));

                    if (isDynamic) {
                        const delta = count - lastCountChangeCount;
                        if (delta >= 10) {
                            lastCountChangeCount = count;
                            this.onCountChange(count, delta);
                        }
                    }
                }
            },
        });
    }

    render() {
        const {
            nowListening,
            connecting,
            aroundTheWorld,
        } = this.props;
        const { count, canClickCount } = this.state;
        return (
            <div
                className="slide fullscreen"
                ref={(ref) => { this.refContainer = ref; }}
            >
                <div className="table fullscreen">
                    <div className="cell middle">
                        <div className="stats">
                            <div
                                className="label"
                                ref={(ref) => { this.refLabel = ref; }}
                            >
                                <div
                                    className="text"
                                    ref={(ref) => { this.refNowListening = ref; }}
                                >
                                    <ReactMarkdown source={nowListening} />
                                </div>
                                <div
                                    className="text"
                                    ref={(ref) => { this.refConnecting = ref; }}
                                >
                                    <ReactMarkdown source={connecting} />
                                </div>
                                <div
                                    className="text"
                                    ref={(ref) => { this.refAroundTheWorld = ref; }}
                                >
                                    <ReactMarkdown source={aroundTheWorld} />
                                </div>
                            </div>
                            <button
                                type="button"
                                className={classNames({
                                    count: true,
                                    active: canClickCount,
                                })}
                                disabled={!canClickCount}
                                ref={(ref) => { this.refCount = ref; }}
                                onClick={this.onClickCount}
                            >{ count }</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

Stats.propTypes = propTypes;
Stats.defaultProps = defaultProps;

export default Stats;



// WEBPACK FOOTER //
// ./components/partials/Stats.jsx